// Bulk voiceline generator — admin convenience client.
//
// Iterates every (voiceId × phraseKey) pair and calls the
// generate-voiceline edge function for each. Idempotent: phrases
// already cached in Storage are skipped (the edge function
// HEAD-checks before re-paying ElevenLabs). Sequential rather than
// parallel — ElevenLabs rate-limits concurrent requests.
//
// Use from AdminPanel or a one-off devtools snippet:
//   const { results } = await bulkGenerateVoicelines({
//     voiceIds: ["voice1", "voice2"],
//     onProgress: ({ done, total, current }) => console.log(...),
//   });

import { supabase } from "./supabase";
import { VOICELINE_KEYS } from "../data/voicelines";

/**
 * Generate a single voiceline. Thin wrapper over the edge function.
 *
 * @param {object} args
 *   @param {string} args.voiceId
 *   @param {string} args.phraseKey
 *   @param {boolean} [args.force]  — re-bake even when cached
 * @returns {Promise<{url: string, cached: boolean}>}
 */
export async function generateVoiceline({ voiceId, phraseKey, voiceSlug, customText, force = false } = {}) {
  if (!voiceId || !phraseKey) {
    throw new Error("voiceId and phraseKey required");
  }
  // customText overrides per-voice variants and the catalog default
  // — admin types in AdminPanel and that text gets baked. voiceSlug
  // resolves the per-voice variant when no customText is provided.
  const body = { voiceId, phraseKey, force };
  if (typeof voiceSlug === "string" && voiceSlug.trim()) {
    body.voiceSlug = voiceSlug.trim().toLowerCase();
  }
  if (typeof customText === "string" && customText.trim()) {
    body.customText = customText.trim();
  }
  const { data, error } = await supabase.functions.invoke("generate-voiceline", {
    body,
  });
  if (error) {
    let detail = error.message || "unknown";
    try {
      const ctx = error?.context;
      if (ctx && typeof ctx.text === "function") {
        const body = await ctx.text();
        const j = JSON.parse(body);
        detail = j?.detail || j?.error || body;
      }
    } catch { /* swallow */ }
    throw new Error(`generate-voiceline failed: ${detail}`);
  }
  return data;
}

/**
 * Bulk-generate every phrase for every supplied voice. Skips already-
 * cached phrases unless force=true.
 *
 * @param {object} args
 *   @param {string[]} args.voiceIds
 *   @param {boolean} [args.force]
 *   @param {(progress: {done:number, total:number, voiceId:string, phraseKey:string, status:"cached"|"generated"|"failed"}) => void} [args.onProgress]
 * @returns {Promise<{ results: Array, failed: Array }>}
 */
export async function bulkGenerateVoicelines({ voices, force = false, onProgress } = {}) {
  if (!Array.isArray(voices) || voices.length === 0) {
    throw new Error("voices[] required (each entry: { voiceId, slug })");
  }
  const total = voices.length * VOICELINE_KEYS.length;
  const results = [];
  const failed = [];
  let done = 0;
  for (const voice of voices) {
    const voiceId = typeof voice === "string" ? voice : voice?.voiceId;
    const voiceSlug = typeof voice === "object" ? voice?.slug : null;
    for (const phraseKey of VOICELINE_KEYS) {
      done += 1;
      try {
        const res = await generateVoiceline({ voiceId, voiceSlug, phraseKey, force });
        results.push({ voiceId, phraseKey, ...res });
        onProgress?.({
          done, total, voiceId, phraseKey,
          status: res.cached ? "cached" : "generated",
        });
      } catch (err) {
        failed.push({ voiceId, phraseKey, error: String(err?.message || err) });
        onProgress?.({ done, total, voiceId, phraseKey, status: "failed" });
      }
    }
  }
  return { results, failed };
}
